import {
  CloudIcon,
  CompassToolIcon,
  CurrencyDollarIcon,
  KeyholeIcon,
  KeyIcon,
  McpIcon,
  PasswordIcon,
  SparkleOutlineIcon,
  UserCircleGearIcon,
  UsersThreeIcon,
} from "@stacklet/ui";

import { SETTINGS_BREADCRUMB } from "app/constants/breadcrumbs";
import { paths } from "app/constants/paths";
import { useConfig } from "app/contexts/ConfigContext";
import { useHasScopePermission } from "app/hooks";

/**
 * Starts the users list's queries when its rail link is hovered.
 *
 * The preload module is imported here rather than at the top of the file, so
 * that its queries stay out of every settings page's chunk. It is small and the
 * import is cached, so the queries still go out well before the click. A
 * preload that fails to load costs nothing: the click fetches as if the hover
 * never happened.
 */
function preloadUsersSettings() {
  import("app/views/settings/access-control/usersSettingsPreload")
    .then((module) => module.preloadUsersSettingsOnHover())
    .catch(() => {});
}

export function useSettingsBreadcrumbs() {
  const config = useConfig();
  const hasPermission = useHasScopePermission();
  const isAdmin = hasPermission("system", "EDIT");

  const adminTabs = isAdmin
    ? [
        {
          label: "Jun0",
          id: "settings-jun0",
          target: paths.settingsJun0,
          section: "AI",
          icon: SparkleOutlineIcon,
        },
        {
          label: "Stacklet MCP",
          id: "settings-mcp",
          target: paths.settingsMcp,
          section: "AI",
          icon: McpIcon,
        },
        {
          label: "Identity Providers",
          id: "settings-identity-providers",
          target: paths.identityProviders,
          section: "Users & Groups",
          icon: KeyIcon,
        },
        {
          label: "Users",
          id: "settings-users",
          target: paths.users,
          section: "Users & Groups",
          icon: UserCircleGearIcon,
          onPreload: preloadUsersSettings,
        },
        {
          label: "Groups",
          id: "settings-groups",
          target: paths.groups,
          section: "Users & Groups",
          icon: UsersThreeIcon,
        },
        {
          label: "Personal API Keys",
          id: "settings-personal-api-keys",
          target: paths.settingsPersonalApiKeys,
          section: "Users & Groups",
          icon: PasswordIcon,
        },
        ...(config?.dev_flags.includes("admin-settings")
          ? [
              {
                label: "Access Model",
                target: paths.accessModel,
                section: "Clouds & Integrations",
                icon: KeyholeIcon,
              },
            ]
          : []),
        {
          label: "Clouds",
          target: paths.settingsClouds,
          section: "Clouds & Integrations",
          icon: CloudIcon,
        },
        {
          label: "Cost",
          target: paths.settingsCost,
          section: "Clouds & Integrations",
          icon: CurrencyDollarIcon,
        },
        {
          label: "Integrations",
          target: paths.settingsIntegrations,
          section: "Clouds & Integrations",
          icon: CompassToolIcon,
        },
      ]
    : [];

  // Mirrors the sidebar: admins land on the users list, everyone else on
  // their profile, the only settings landing page they can open.
  const settingsCrumb = isAdmin
    ? SETTINGS_BREADCRUMB
    : { ...SETTINGS_BREADCRUMB, target: paths.settingsProfile };

  return [settingsCrumb, { tabs: adminTabs }];
}
